import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { lazy, memo, Suspense, useCallback, useEffect } from 'react';
import './download.sass';
import * as classnames from 'classnames';
import { rusDateOnly } from 'utils';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useAPI } from 'console/shared/api/api-context';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { getPhotosByPeriodFromServer } from 'entities/device.photos/model/get-photos-by-period-action';
import { useAction, usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { select_maxtime, select_mintime } from 'entities/device.map.track/lib/selectors';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { useAnchor } from 'shared/hooks/utils';
function get_filename(device_name, minTime, maxTime, file_ext) {
    const date_from = rusDateOnly(new Date(minTime * 1e3));
    const date_to = rusDateOnly(new Date(maxTime * 1e3));
    let filename = `${device_name} ${date_from}`;
    if (date_to !== date_from) {
        filename += `–${date_to}`;
    }
    if (file_ext === 'KML') {
        filename += '.kml';
    }
    else if (file_ext === 'GPX') {
        filename += '.gpx';
    }
    return filename;
}
const CameraContentDialog = lazy(async () => import('entities/device.photos/ui/dialogs'));
function DownloadTrackButtonImpl(props) {
    const { available_photos, device_id, device_name } = props;
    const api = useAPI();
    const mintime = usePartialAppSelector(select_mintime, device_id) ?? 0;
    const maxtime = usePartialAppSelector(select_maxtime, device_id) ?? 0;
    const [anchorEl, open, close] = useAnchor();
    const [opened_camera_content_dialog, closeCameraContentsDialog, openCameraContentsDialog,] = useBooleanState(false);
    const onGetPhotosByPeriod = useAction(getPhotosByPeriodFromServer);
    useEffect(() => {
        if (device_id && available_photos) {
            onGetPhotosByPeriod(device_id, [mintime, maxtime]);
        }
    }, [device_id, mintime, maxtime]);
    const downloadFile = useCallback((fileExt, method) => {
        return (window.location.href = api.get_url(method, {
            device_id: `${device_id}`,
            mintime: `${mintime}`,
            maxtime: `${maxtime}`,
            filename: get_filename(device_name, mintime, maxtime, fileExt),
        }, true));
    }, [api]);
    const downloadGeozoneKML = useCallback((method) => {
        return (window.location.href = api.get_url(method, {
            filename: 'geozones.kml',
        }, true));
    }, [api]);
    const downloadKMLButton = useCallback(() => {
        downloadFile('KML', 'download_kml');
        close();
    }, [downloadFile]);
    const downloadGPXButton = useCallback(() => {
        downloadFile('GPX', 'download_gpx');
        close();
    }, []);
    const downloadPhotoReportButton = useCallback(() => {
        openCameraContentsDialog();
        close();
    }, [openCameraContentsDialog]);
    const downloadKMLGeozones = useCallback(() => {
        downloadGeozoneKML('download_kml_geozones');
        close();
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(IconButton, { disableRipple: true, "aria-controls": "download-path-menu", "aria-haspopup": "true", style: { padding: '0', minWidth: '20px' }, className: classnames('ui-map-download', 'ui-icon-map-button-download', { active: Boolean(anchorEl) }), title: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u0434\u0430\u043D\u043D\u044B\u0435", onClick: open, size: "large" }), _jsxs(Menu, { id: "simple-menu", anchorEl: anchorEl, keepMounted: true, open: Boolean(anchorEl), onClose: close, children: [_jsx(MenuItem, { onClick: downloadKMLButton, children: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u043C\u0430\u0440\u0448\u0440\u0443\u0442 \u0432 \u0444\u043E\u0440\u043C\u0430\u0442\u0435 KML" }), _jsx(MenuItem, { onClick: downloadGPXButton, children: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u043C\u0430\u0440\u0448\u0440\u0443\u0442 \u0432 \u0444\u043E\u0440\u043C\u0430\u0442\u0435 GPX" }), available_photos && (_jsxs("div", { children: [_jsx(MenuItem, { onClick: downloadPhotoReportButton, children: "\u0421\u043D\u0438\u043C\u043A\u0438 \u0441 \u043A\u0430\u043C\u0435\u0440\u044B" }), _jsx(ErrorBoundary, { children: _jsx(Suspense, { fallback: _jsx(MessageBox, { open: true, isInfo: true, title: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430..." }), children: _jsx(CameraContentDialog, { onClose: closeCameraContentsDialog, open: opened_camera_content_dialog, device_id: device_id, api: api, mintime: mintime, maxtime: maxtime }) }) })] })), _jsx(MenuItem, { onClick: downloadKMLGeozones, children: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u0433\u0435\u043E\u0437\u043E\u043D\u044B \u0432 \u0444\u043E\u0440\u043C\u0430\u0442\u0435 KML" })] })] }));
}
export const DownloadTrackButton = memo(DownloadTrackButtonImpl);
